import axios from '@/plugins/axios'
import { Module, useStore } from 'vuex'

export interface SocialActivity {
    username: string
    token: string
}


const activityModule: Module<any, any> = {
    state: {
        activities: [],
        posts: [],
        content: [],
        members: [],
        profiles: [],
        orgProfiles: [],
        stats: {},
        totalRows: 0,
        filters: {},
    },
    actions: {
        async getActivities({ commit, dispatch }, params) {
        
            let filters = ''
            // build filters
            filters += params?.perPage ? `itemPerPage=${params.perPage}` : ''
            filters += params?.page ? `&page=${params.page}` : ''
            filters += params?.search ? `&search=${params.search}` : ''
            filters += params?.activityType
                ? `&activityType=${params.activityType}`
                : ''
            filters += params?.syncStatus
                ? `&syncStatus=${params.syncStatus}`
                : ''
            filters += params?.companyType
                ? `&company=${params.companyType}`
                : ''
            filters +=
                params?.workspaceMemberId
                    ? `&workspaceMemberId=${params.workspaceMemberId}`
                    : ''
            filters += params?.contentId ? `&contentId=${params.contentId}` : ''
            filters += params?.postId ? `&postId=${params.postId}` : ''
            filters += params?.postAuthorActivity
                ? `&postAuthorActivity=${params.postAuthorActivity}`
                : ''
            filters += params?.sortKey ? `&sort=${params.sortKey}` : ''
            filters += params?.sortOrder ? `&order=${params.sortOrder}` : ''
            filters += params?.profileUuid ? `&profileUuid=${params.profileUuid}`: ''

            if (params?.from && params?.to) {
                filters += params?.from ? `&dateRange[from]=${params.from}` : ''
                filters += params?.to ? `&dateRange[to]=${params.to}` : ''
            }

            const { data } = await axios.get(
                `/api/social_activities?${filters}`,
            )

            if (params.loadAssociations) {
                commit('setActivities', data ? data['hydra:member'] : null)
                commit('setTotalRows', data ? data['hydra:totalItems'] : null)
                await dispatch('loadRelatedActivityData')
            }

            return data
        },

        async exportActivities(_, params) {
            let filters = ''

            filters += params?.perPage || params?.perPage===0 ? `?itemPerPage=${params.perPage}` : '?'
            filters += params?.search ? `&search=${params.search}` : ''
            filters += params?.activityType
                ? `&activityType=${params.activityType}`
                : ''
            filters += params?.syncStatus
                ? `&syncStatus=${params.syncStatus}`
                : ''
            filters += params?.companyType
                ? `&company=${params.companyType}`
                : ''
            filters +=
                params?.workspaceMemberId
                    ? `&workspaceMemberId=${params.workspaceMemberId}`
                    : ''
            filters += params?.contentId ? `&contentId=${params.contentId}` : ''
            filters += params?.postId ? `&postId=${params.postId}` : ''
            filters += params?.postAuthorActivity
                ? `&postAuthorActivity=${params.postAuthorActivity}`
                : ''
            filters += params?.sortKey ? `&sort=${params.sortKey}` : ''
            filters += params?.sortOrder ? `&order=${params.sortOrder}` : ''
            filters += params?.networkUserId ? `&networkUserid=${params.networkUserId}`: ''

            if (params?.from && params?.to) {
                filters += params?.from ? `&dateRange[from]=${params.from}` : ''
                filters += params?.to ? `&dateRange[to]=${params.to}` : ''
            }

            const { data } = await axios.get(
                `/api/social_activities/export${filters}`,
            )

            return data
        },
        async getSocialActivity(_,{id}){
            const { data } = await axios.get(
                `/api/social_activities/${id}`,
            )

            return data
        },

    	async loadRelatedActivityData({commit, dispatch, state}) {
    	
    		if (!state.activities) {
    			throw 'Load activities first!'
    		}

		    const profileUuids = [ ... new Set( state.activities.map(a => a.profileUuid).filter(i => i) ) ]
    		const workspaceMemberIds = [ ... new Set( state.activities.map(a => a.posterWorkspaceMemberId).filter(i => i) ) ]
    		const contentIds = [ ... new Set( state.activities.map(a => a.contentId).filter(i => i) ) ]
    		const postIds = [ ... new Set( state.activities.map(a => a.postId).filter(i => i) ) ]
    	
    		if (workspaceMemberIds.length) {
                await dispatch('getMembers', {
                    workspaceMemberIds,
                }).then(r => {
                    commit('setActivityMembers', r.items)
                })
            }
		
            if (profileUuids.length) {
                await dispatch('getProfiles', {
                    uuids: profileUuids,
                }).then(async r => {
                    commit('setActivityProfiles', r['hydra:member'])
                    const orgProfileUuids = [ ... new Set(r['hydra:member'].map(p => p.identity.orgUuid).filter(u => u)) ]
                    if (orgProfileUuids.length) {
                        await dispatch('getProfiles', {
                            uuids: orgProfileUuids,
                        }).then(r => {
                            commit('setActivityOrgProfiles', r['hydra:member'])
                        })
                    }
                })
            }
		
            if (contentIds.length) {
                await dispatch('getContents', {
                    contentIds,
                }).then(r => {
                    commit('setActivityContent', r['hydra:member'])
                })
            }
		
            if (postIds.length) {
                await dispatch('getPosts', {
                    postIds: postIds,
                }).then(r => {
                    commit('setActivityPosts', r['hydra:member'])
                })
            }
		
    	},

    },
    getters: {
    	getActivities(state) {
    		return state.activities
    	},
    	getActivityMember: (state) => (id) => {
    		return state.members?.find(member => member.id === id)
    	},
    	getActivityProfile: (state) => (uuid) => {
    		return state.profiles?.find(profile => profile.uuid === uuid)
    	},
    	getActivityOrgProfile: (state) => (uuid) => {
    		return state.orgProfiles?.find(profile => profile.uuid === uuid)
    	},
    	getActivityContent: (state) => (id) => {
    		return state.content?.find(content => content.id === id)
    	},
    	getActivityPost: (state) => (id) => {
    		return state.posts?.find(post => post.id === id)
    	},
    },
    mutations: {
        setActivities(state: any, activities) {
            state.activities = activities
        },
        setActivityMembers(state: any, members) {
            state.members = members
        },
        setActivityProfiles(state: any, profiles) {
            state.profiles = profiles
        },
        setActivityOrgProfiles(state: any, profiles) {
            state.orgProfiles = profiles
        },
        setActivityContent(state: any, content) {
            state.content = content
        },
        setActivityPosts(state: any, posts) {
            state.posts = posts
        },
        setReps(state: any, reps) {
            state.reps = reps
        },
        setTotalRows(state: any, rows: number) {
            state.totalRows = rows
        },
        clearActivityFilters(state: any) {
            state.filters = {}
        },
    },
}

export default activityModule
